// 🎭 HeroSection - Composant de section hero réutilisable
// Section principale avec gradient, badge et features optionnels
// Utilisé dans : HomePage, AboutPage, PartnersPage

import { CheckCircle } from 'lucide-react'

/**
 * Composant Hero Section uniforme pour toutes les pages
 * Crée une section d'introduction percutante avec design moderne
 * 
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.badge - Badge optionnel { icon: Component, text: string }
 * @param {string} props.title - Titre principal de la section
 * @param {string|string[]} props.description - Description (string ou array de paragraphes)
 * @param {Array} props.features - Listes de features optionnels [{ icon: Component, text: string }]
 * @param {string} props.gradientClass - Classes Tailwind pour le gradient personnalisé
 */
export default function HeroSection({ 
  badge, 
  title, 
  description, 
  features = [],
  gradientClass = "from-primary-500 via-primary-600 to-neutral-900"
}) {
  // Extraction de l'icône du badge et normalisation de la description
  const BadgeIcon = badge?.icon
  const descriptionArray = Array.isArray(description) ? description : [description]

  return (
    // Section hero avec gradient et pattern subtil
    <section className={`relative py-20 md:py-32 bg-gradient-to-br ${gradientClass} text-white overflow-hidden`}>
      {/* Pattern de fond SVG pour la texture */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container-custom px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge optionnel avec effet glassmorphism */}
          {badge && (
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
              {BadgeIcon && <BadgeIcon className="text-white" size={20} />}
              <span className="text-sm font-medium">{badge.text}</span>
            </div>
          )}

          {/* Titre principal avec typographie display */}
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 tracking-wide">
            {title}
          </h1>

          {/* Description avec support multi-paragraphes */}
          <div className="space-y-4 text-lg md:text-xl text-neutral-100 leading-relaxed mb-8">
            {descriptionArray.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {/* Features Pills */}
          {features.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              {features.map((feature, index) => {
                const FeatureIcon = feature.icon || CheckCircle
                return (
                  <div 
                    key={index} 
                    className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2 rounded-full"
                  >
                    <FeatureIcon className="text-primary-300" size={20} />
                    <span className="font-medium">{feature.text}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
